import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '@/lib/auth-context';
import { feesAPI, invoicesAPI, clientsAPI, firmsAPI, serviceDescAPI, clientFeesAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, IndianRupee, CheckCircle, XCircle, X, FileText, AlertCircle, Pencil, Trash2, Download, Upload } from 'lucide-react';
import { InvoicesTable } from './components/InvoicesTable';
import { FirmsTab } from './components/FirmsTab';
import { InvoiceDetailDialog } from './components/InvoiceDetailDialog';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const FEE_STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const INVOICE_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
};

const DEFAULT_SERVICE_DESCRIPTIONS = [
  'GSTR-1', 'GSTR-3B', 'GST Compliances', 'GSTR-9', 'GSTR-9C',
  'ROC Compliances', 'RBI Compliances', 'Income Tax Return',
  'Consultancy Fees', 'Retainership Fees', 'GST Registration',
  'Reimbursement of Expenses', 'Digital Signature', 'Professional Fees',
];

const todayStr = new Date().toISOString().split('T')[0];
const DEFAULT_INVOICE = {
  client_id: '', firm_id: '', invoice_series: 'GRW',
  invoice_date: todayStr,
  due_date: todayStr, service_description: '',
  service_period_from: '', service_period_to: '',
  amount: '', cgst_rate: '9', sgst_rate: '9', igst_rate: '0',
};

const PERIOD_OPTIONS = (() => {
  const opts = [];
  for (let y = 2024; y <= 2028; y++) {
    for (let m = 1; m <= 12; m++) {
      const val = `${y}-${String(m).padStart(2, '0')}`;
      const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      opts.push({ value: val, label: `${names[m - 1]} ${y}` });
    }
  }
  return opts;
})();

const fmtCurrency = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '0.00';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
function round2(v) { return Math.round(v * 100) / 100; }

function calcPeriodCount(from, to) {
  if (!from) return 1;
  if (!to || to === from) return 1;
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return (ty - fy) * 12 + (tm - fm) + 1;
}

const BillingPage = () => {
  const { user } = useAuth();
  const [fees, setFees] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [firms, setFirms] = useState([]);
  const [customDescs, setCustomDescs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [firmDialogOpen, setFirmDialogOpen] = useState(false);
  const [editFirm, setEditFirm] = useState(null);
  const [newFee, setNewFee] = useState({ client_id: '', amount: '', description: '' });
  const [newInvoice, setNewInvoice] = useState({ ...DEFAULT_INVOICE });
  const [newFirm, setNewFirm] = useState({ name: '', address: '', gstin: '', invoice_prefix: '', pan: '', email: '', phone: '', bank_name: '', bank_ifsc: '', bank_account: '', bank_accounts: [], msme_number: '', cin_number: '' });
  const [uninvoicedItems, setUninvoicedItems] = useState([]);
  const [allUninvoiced, setAllUninvoiced] = useState([]);
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [newDescName, setNewDescName] = useState('');
  const [descDialogOpen, setDescDialogOpen] = useState(false);
  const [clientFees, setClientFees] = useState([]);
  const [feeStructureDialogOpen, setFeeStructureDialogOpen] = useState(false);
  const [newClientFee, setNewClientFee] = useState({ client_id: '', service_name: '', amount: '', notes: '' });
  const [editClientFee, setEditClientFee] = useState(null);
  const [feeFilterClient, setFeeFilterClient] = useState('all');
  const bulkFeeInputRef = useRef(null);
  const amountRef = useRef('');

  const canCreate = ['admin', 'staff'].includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  const isClient = user?.role === 'client';

  const allDescriptions = useMemo(() => {
    const custom = customDescs.map(d => d.name);
    const merged = [...DEFAULT_SERVICE_DESCRIPTIONS, ...custom.filter(c => !DEFAULT_SERVICE_DESCRIPTIONS.includes(c))];
    return merged.sort();
  }, [customDescs]);

  const loadData = useCallback(async () => {
    try {
      const promises = [feesAPI.list(), invoicesAPI.list()];
      if (canCreate) promises.push(clientsAPI.list());
      if (isAdmin) promises.push(firmsAPI.list(), serviceDescAPI.list());
      const results = await Promise.all(promises);
      setFees(results[0].data);
      setInvoices(results[1].data);
      if (canCreate) setClients(results[2].data);
      if (isAdmin) {
        setFirms(results[3].data);
        setCustomDescs(results[4].data);
      }
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, [canCreate, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load all uninvoiced items for the Pending tab
  useEffect(() => {
    if (!canCreate) return;
    invoicesAPI.uninvoicedSubtasks({}).then(res => setAllUninvoiced(res.data)).catch(() => {});
  }, [canCreate, invoices]);

  // Load uninvoiced items for selected client in invoice form
  useEffect(() => {
    if (!newInvoice.client_id) { setUninvoicedItems([]); setSelectedLinks([]); return; }
    invoicesAPI.uninvoicedSubtasks({ client_id: newInvoice.client_id })
      .then(res => setUninvoicedItems(res.data))
      .catch(() => setUninvoicedItems([]));
    setSelectedLinks([]);
  }, [newInvoice.client_id]);

  // Load client fees for Fee Structure tab
  useEffect(() => {
    if (!canCreate) return;
    clientFeesAPI.list().then(res => setClientFees(res.data)).catch(() => {});
  }, [canCreate]);

  // Auto-fill amount from client fee structure (rate × number of periods)
  useEffect(() => { amountRef.current = newInvoice.amount; }, [newInvoice.amount]);
  useEffect(() => {
    if (!newInvoice.client_id || !newInvoice.service_description) return;
    clientFeesAPI.lookup(newInvoice.client_id, newInvoice.service_description)
      .then(res => {
        if (res.data.amount != null && !amountRef.current) {
          const rate = res.data.amount;
          const periodCount = calcPeriodCount(newInvoice.service_period_from, newInvoice.service_period_to);
          const totalAmount = rate * periodCount;
          setNewInvoice(prev => ({ ...prev, amount: String(totalAmount) }));
        }
      })
      .catch(() => {});
  }, [newInvoice.client_id, newInvoice.service_description]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalculate amount when periods change (if fee rate exists)
  useEffect(() => {
    if (!newInvoice.client_id || !newInvoice.service_description) return;
    clientFeesAPI.lookup(newInvoice.client_id, newInvoice.service_description)
      .then(res => {
        if (res.data.amount != null) {
          const rate = res.data.amount;
          const periodCount = calcPeriodCount(newInvoice.service_period_from, newInvoice.service_period_to);
          const totalAmount = rate * periodCount;
          setNewInvoice(prev => ({ ...prev, amount: String(totalAmount) }));
        }
      })
      .catch(() => {});
  }, [newInvoice.service_period_from, newInvoice.service_period_to]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tax calculation
  const taxCalc = useMemo(() => {
    const amt = parseFloat(newInvoice.amount) || 0;
    const cgst = round2(amt * (parseFloat(newInvoice.cgst_rate) || 0) / 100);
    const sgst = round2(amt * (parseFloat(newInvoice.sgst_rate) || 0) / 100);
    const igst = round2(amt * (parseFloat(newInvoice.igst_rate) || 0) / 100);
    return { cgst, sgst, igst, total: round2(amt + cgst + sgst + igst) };
  }, [newInvoice.amount, newInvoice.cgst_rate, newInvoice.sgst_rate, newInvoice.igst_rate]);

  // Fee handlers
  const handleCreateFee = async (e) => {
    e.preventDefault();
    try {
      await feesAPI.create({ ...newFee, amount: parseFloat(newFee.amount) });
      toast.success('Fee proposal created');
      setFeeDialogOpen(false);
      setNewFee({ client_id: '', amount: '', description: '' });
      loadData();
    } catch (err) { toast.error('Failed to create fee proposal'); }
  };
  const handleFeeAction = async (feeId, action) => {
    try { await feesAPI.action(feeId, { action }); toast.success(`Fee ${action}`); loadData(); }
    catch (err) { toast.error(`Failed to ${action} fee`); }
  };

  // Invoice handlers
  const fmtPeriod = (p) => {
    if (!p) return '';
    if (p.startsWith('FY')) return `FY ${p.replace('FY', '')}`;
    if (p.includes('-Q')) { const [y, q] = p.split('-Q'); return `Q${q} ${y}`; }
    if (p.length === 7) {
      const [y, m] = p.split('-');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[parseInt(m)-1]} ${y}`;
    }
    return p;
  };

  const toggleLink = (item) => {
    setSelectedLinks(prev => {
      const key = `${item.subtask_id}`;
      const exists = prev.find(l => l.subtask_id === item.subtask_id);
      if (exists) return prev.filter(l => l.subtask_id !== item.subtask_id);
      return [...prev, {
        task_id: item.task_id,
        subtask_id: item.subtask_id,
        subtask_title: item.subtask_title,
        from_period: item.from_period,
        to_period: item.to_period,
      }];
    });
  };

  const handleSaveInvoice = async (e) => {
    e.preventDefault();
    if (!newInvoice.client_id || !newInvoice.amount || !newInvoice.service_description) {
      toast.error('Fill all required fields'); return;
    }
    let periodStr = null;
    if (newInvoice.service_period_from && newInvoice.service_period_to) {
      periodStr = `${fmtPeriod(newInvoice.service_period_from)} to ${fmtPeriod(newInvoice.service_period_to)}`;
    } else if (newInvoice.service_period_from) {
      periodStr = fmtPeriod(newInvoice.service_period_from);
    }
    const payload = {
      client_id: newInvoice.client_id,
      firm_id: newInvoice.firm_id || null,
      invoice_series: newInvoice.invoice_series.trim() || 'GRW',
      invoice_date: newInvoice.invoice_date,
      due_date: newInvoice.invoice_date,
      service_description: newInvoice.service_description,
      service_period: periodStr,
      amount: parseFloat(newInvoice.amount),
      cgst_rate: parseFloat(newInvoice.cgst_rate) || 0,
      sgst_rate: parseFloat(newInvoice.sgst_rate) || 0,
      igst_rate: parseFloat(newInvoice.igst_rate) || 0,
      linked_subtasks: selectedLinks.length > 0 ? selectedLinks.map(l => ({
        task_id: l.task_id,
        subtask_id: l.subtask_id,
        from_period: l.from_period,
        to_period: l.to_period,
      })) : null,
    };
    try {
      if (editInvoice) {
        await invoicesAPI.update(editInvoice.id, payload);
        toast.success('Invoice updated');
      } else {
        await invoicesAPI.create(payload);
        toast.success('Invoice created');
      }
      setInvoiceDialogOpen(false);
      setEditInvoice(null);
      setNewInvoice({ ...DEFAULT_INVOICE });
      setSelectedLinks([]);
      loadData();
    } catch (err) { toast.error(editInvoice ? 'Failed to update' : 'Failed to create'); }
  };

  const handleEditInvoice = (inv) => {
    setEditInvoice(inv);
    let pFrom = '', pTo = '';
    if (inv.service_period) {
      const match = inv.service_period.match(/^(\w+ \d{4}) to (\w+ \d{4})$/);
      if (match) {
        const parseLabel = (lbl) => {
          const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const [mn, yr] = lbl.split(' ');
          const idx = names.indexOf(mn);
          return idx >= 0 ? `${yr}-${String(idx + 1).padStart(2, '0')}` : '';
        };
        pFrom = parseLabel(match[1]);
        pTo = parseLabel(match[2]);
      }
    }
    setNewInvoice({
      client_id: inv.client_id || '', firm_id: inv.firm_id || '',
      invoice_series: inv.invoice_series || 'GRW',
      invoice_date: inv.invoice_date || '', due_date: inv.invoice_date || inv.due_date || '',
      service_description: inv.service_description || '',
      service_period_from: pFrom, service_period_to: pTo,
      amount: String(inv.amount || ''), cgst_rate: String(inv.cgst_rate ?? 9),
      sgst_rate: String(inv.sgst_rate ?? 9), igst_rate: String(inv.igst_rate ?? 0),
    });
    setInvoiceDialogOpen(true);
  };

  const handleDeleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    try { await invoicesAPI.delete(inv.id); toast.success('Invoice deleted'); loadData(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const handleDownloadPdf = async (inv) => {
    try {
      const res = await invoicesAPI.downloadPdf(inv.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number.replace(/\//g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) { toast.error('Failed to download PDF'); }
  };

  const handleInvoiceStatus = async (invoiceId, status) => {
    try { await invoicesAPI.update(invoiceId, { status }); toast.success(`Invoice marked as ${status}`); loadData(); }
    catch (err) { toast.error('Failed to update'); }
  };

  // Firm handlers
  const EMPTY_FIRM = { name: '', address: '', gstin: '', invoice_prefix: '', pan: '', email: '', phone: '', bank_name: '', bank_ifsc: '', bank_account: '', bank_accounts: [], msme_number: '', cin_number: '' };

  const handleSaveFirm = async (e) => {
    e.preventDefault();
    try {
      let firmId;
      if (editFirm) {
        await firmsAPI.update(editFirm.id, newFirm);
        firmId = editFirm.id;
        toast.success('Firm updated');
      } else {
        const res = await firmsAPI.create(newFirm);
        firmId = res.data.id;
        toast.success('Firm created');
      }
      if (logoFile && firmId) {
        await firmsAPI.uploadLogo(firmId, logoFile);
        toast.success('Logo uploaded');
      }
      setFirmDialogOpen(false);
      setEditFirm(null);
      setNewFirm({ ...EMPTY_FIRM });
      setLogoFile(null);
      loadData();
    } catch (err) { toast.error('Failed to save firm'); }
  };

  const handleEditFirm = (f) => {
    setEditFirm(f);
    setNewFirm({ name: f.name || '', address: f.address || '', gstin: f.gstin || '', invoice_prefix: f.invoice_prefix || '', pan: f.pan || '', email: f.email || '', phone: f.phone || '', bank_name: f.bank_name || '', bank_ifsc: f.bank_ifsc || '', bank_account: f.bank_account || '', bank_accounts: f.bank_accounts || [], msme_number: f.msme_number || '', cin_number: f.cin_number || '' });
    setLogoFile(null);
    setFirmDialogOpen(true);
  };

  const handleDeleteFirm = async (f) => {
    if (!window.confirm(`Delete firm "${f.name}"?`)) return;
    try { await firmsAPI.delete(f.id); toast.success('Firm deleted'); loadData(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  // Custom description handlers
  const handleAddDesc = async () => {
    if (!newDescName.trim()) return;
    try { await serviceDescAPI.create({ name: newDescName.trim() }); toast.success('Added'); setNewDescName(''); loadData(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteDesc = async (d) => {
    try { await serviceDescAPI.delete(d.id); toast.success('Removed'); loadData(); }
    catch (err) { toast.error('Failed'); }
  };

  // Client fee handlers
  const loadClientFees = () => clientFeesAPI.list().then(res => setClientFees(res.data)).catch(() => {});
  const handleSaveClientFee = async (e) => {
    e.preventDefault();
    if (!newClientFee.client_id || !newClientFee.service_name || !newClientFee.amount) {
      toast.error('Fill client, service and amount'); return;
    }
    try {
      if (editClientFee) {
        await clientFeesAPI.update(editClientFee.id, { amount: parseFloat(newClientFee.amount), notes: newClientFee.notes || null });
      } else {
        await clientFeesAPI.create({ client_id: newClientFee.client_id, service_name: newClientFee.service_name, amount: parseFloat(newClientFee.amount), notes: newClientFee.notes || null });
      }
      toast.success(editClientFee ? 'Fee updated' : 'Fee saved');
      setEditClientFee(null);
      setNewClientFee({ client_id: '', service_name: '', amount: '', notes: '' });
      setFeeStructureDialogOpen(false);
      loadClientFees();
    } catch (err) { toast.error('Failed to save fee'); }
  };
  const handleDeleteClientFee = async (f) => {
    if (!window.confirm(`Remove fee for ${f.client_name || 'this client'} — ${f.service_name}?`)) return;
    try { await clientFeesAPI.delete(f.id); toast.success('Fee removed'); loadClientFees(); }
    catch (err) { toast.error('Failed'); }
  };

  const filteredToOptions = useMemo(() =>
    PERIOD_OPTIONS.filter(o => !newInvoice.service_period_from || o.value >= newInvoice.service_period_from),
    [newInvoice.service_period_from]
  );

  const filteredClientFees = feeFilterClient === 'all' ? clientFees : clientFees.filter(f => f.client_id === feeFilterClient);

  const handleDownloadFeeTemplate = async () => {
    try {
      const res = await clientFeesAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fee_structure_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) { toast.error('Failed to download template'); }
  };

  const handleBulkFeeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await clientFeesAPI.bulkUpload(file);
      toast.success(res.data.message);
      loadClientFees();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    e.target.value = '';
  };

  const getClientName = (cid) => clients.find(c => c.id === cid)?.name || cid?.substring(0, 8) || '-';
  const getFirmName = (fid) => firms.find(f => f.id === fid)?.name || '';

  if (loading) return <div className="h-64 bg-muted rounded-xl animate-pulse" />;

  // Invoice form JSX (shared for create + edit)
  const invoiceFormJSX = (
    <form onSubmit={handleSaveInvoice} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Client *</Label>
          <Select value={newInvoice.client_id} onValueChange={v => setNewInvoice({ ...newInvoice, client_id: v })}>
            <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
            <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Billing Firm</Label>
          <Select value={newInvoice.firm_id || '_none'} onValueChange={v => {
            const fid = v === '_none' ? '' : v;
            const selectedFirm = firms.find(f => f.id === fid);
            const prefix = selectedFirm?.invoice_prefix || newInvoice.invoice_series;
            setNewInvoice({ ...newInvoice, firm_id: fid, invoice_series: prefix });
          }}>
            <SelectTrigger data-testid="invoice-firm-select"><SelectValue placeholder="Select firm" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">No firm (default)</SelectItem>
              {firms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Invoice Series</Label>
          <Input value={newInvoice.invoice_series} onChange={e => setNewInvoice({ ...newInvoice, invoice_series: e.target.value })} placeholder="GRW" data-testid="invoice-series-input" />
        </div>
        <div>
          <Label>Invoice Date *</Label>
          <Input type="date" value={newInvoice.invoice_date} onChange={e => setNewInvoice({ ...newInvoice, invoice_date: e.target.value, due_date: e.target.value })} required data-testid="invoice-date-input" />
        </div>
        <div>
          <Label>Due Date</Label>
          <Input type="date" value={newInvoice.due_date || newInvoice.invoice_date} readOnly className="bg-muted/50 cursor-not-allowed" data-testid="invoice-due-date-input" />
          <p className="text-[10px] text-muted-foreground mt-0.5">Same as invoice date</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Description of Service *</Label>
          <Select value={newInvoice.service_description} onValueChange={v => setNewInvoice({ ...newInvoice, service_description: v })}>
            <SelectTrigger data-testid="invoice-description-input"><SelectValue placeholder="Select service" /></SelectTrigger>
            <SelectContent>{allDescriptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Period From</Label>
          <Select value={newInvoice.service_period_from} onValueChange={v => setNewInvoice({ ...newInvoice, service_period_from: v })}>
            <SelectTrigger data-testid="invoice-period-from"><SelectValue placeholder="From month" /></SelectTrigger>
            <SelectContent>{PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Period To</Label>
          <Select value={newInvoice.service_period_to} onValueChange={v => setNewInvoice({ ...newInvoice, service_period_to: v })}>
            <SelectTrigger data-testid="invoice-period-to"><SelectValue placeholder="To month" /></SelectTrigger>
            <SelectContent>{filteredToOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {/* Link Filed Tasks/Subtasks */}
      {uninvoicedItems.length > 0 && !editInvoice && (
        <div className="border rounded-lg p-3 space-y-2">
          <Label className="text-xs font-semibold flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Link Completed Tasks
          </Label>
          <p className="text-[10px] text-muted-foreground">Select filed subtasks to link to this invoice. They will be marked as invoiced.</p>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {uninvoicedItems.map(item => {
              const isSelected = selectedLinks.some(l => l.subtask_id === item.subtask_id);
              return (
                <label key={item.subtask_id} className={`flex items-center gap-2 px-2.5 py-1.5 rounded border cursor-pointer transition-colors ${isSelected ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50 border-transparent'}`} data-testid={`link-subtask-${item.subtask_id}`}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggleLink(item)} className="rounded" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{item.subtask_title}</p>
                    <p className="text-[10px] text-muted-foreground">{item.board_name} &middot; {fmtPeriod(item.from_period)} to {fmtPeriod(item.to_period)} &middot; {item.filed_periods.length} period(s)</p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
      <div>
        <Label>Amount (Rs.) *</Label>
        <Input type="number" value={newInvoice.amount} onChange={e => setNewInvoice({ ...newInvoice, amount: e.target.value })} required min="0" step="0.01" placeholder="0.00" data-testid="invoice-amount-input" />
      </div>
      <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
        <h4 className="text-sm font-semibold">GST Calculation</h4>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">CGST (%)</Label>
            <Input type="number" value={newInvoice.cgst_rate} onChange={e => setNewInvoice({ ...newInvoice, cgst_rate: e.target.value })} min="0" max="100" step="0.01" data-testid="invoice-cgst-rate" />
            <p className="text-[11px] text-muted-foreground mt-0.5">= Rs. {fmtCurrency(taxCalc.cgst)}</p>
          </div>
          <div>
            <Label className="text-xs">SGST (%)</Label>
            <Input type="number" value={newInvoice.sgst_rate} onChange={e => setNewInvoice({ ...newInvoice, sgst_rate: e.target.value })} min="0" max="100" step="0.01" data-testid="invoice-sgst-rate" />
            <p className="text-[11px] text-muted-foreground mt-0.5">= Rs. {fmtCurrency(taxCalc.sgst)}</p>
          </div>
          <div>
            <Label className="text-xs">IGST (%)</Label>
            <Input type="number" value={newInvoice.igst_rate} onChange={e => setNewInvoice({ ...newInvoice, igst_rate: e.target.value })} min="0" max="100" step="0.01" data-testid="invoice-igst-rate" />
            <p className="text-[11px] text-muted-foreground mt-0.5">= Rs. {fmtCurrency(taxCalc.igst)}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between border-t pt-3">
        <span className="font-semibold text-sm">Invoice Value</span>
        <span className="text-lg font-bold text-teal-700" data-testid="invoice-total-value">Rs. {fmtCurrency(taxCalc.total)}</span>
      </div>
      <Button type="submit" className="w-full" data-testid="invoice-submit-button">{editInvoice ? 'Update Invoice' : 'Create Invoice'}</Button>
    </form>
  );

  return (
    <div className="space-y-6" data-testid="billing-page">
      <Tabs defaultValue="invoices">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="invoices" data-testid="billing-invoices-tab">Invoices</TabsTrigger>
            <TabsTrigger value="pending" data-testid="billing-pending-tab">
              Pending Invoicing
              {allUninvoiced.length > 0 && <Badge variant="destructive" className="ml-1.5 h-4 text-[9px] px-1.5">{allUninvoiced.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="proposals" data-testid="billing-proposals-tab">Fee Proposals</TabsTrigger>
            {canCreate && <TabsTrigger value="fee-structure" data-testid="billing-fee-structure-tab">Fee Structure</TabsTrigger>}
            {isAdmin && <TabsTrigger value="firms" data-testid="billing-firms-tab">Firms</TabsTrigger>}
          </TabsList>
          {canCreate && (
            <div className="flex gap-2">
              {isAdmin && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setDescDialogOpen(true)} data-testid="manage-descriptions-button" className="text-xs">
                    <Plus className="h-3 w-3 mr-1" /> Service Items
                  </Button>
                  <Dialog open={feeDialogOpen} onOpenChange={setFeeDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="secondary" data-testid="create-fee-button"><Plus className="h-4 w-4 mr-1" /> Fee Proposal</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Create Fee Proposal</DialogTitle></DialogHeader>
                      <form onSubmit={handleCreateFee} className="space-y-4">
                        <div>
                          <Label>Client *</Label>
                          <Select value={newFee.client_id} onValueChange={v => setNewFee({ ...newFee, client_id: v })}>
                            <SelectTrigger data-testid="fee-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                            <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Amount (Rs.) *</Label><Input type="number" value={newFee.amount} onChange={e => setNewFee({ ...newFee, amount: e.target.value })} required min="0" step="0.01" data-testid="fee-amount-input" /></div>
                        <div><Label>Description</Label><Textarea value={newFee.description} onChange={e => setNewFee({ ...newFee, description: e.target.value })} /></div>
                        <Button type="submit" className="w-full" data-testid="fee-submit-button">Create Proposal</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  <Dialog open={invoiceDialogOpen} onOpenChange={(open) => { setInvoiceDialogOpen(open); if (!open) { setEditInvoice(null); setNewInvoice({ ...DEFAULT_INVOICE }); } }}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="invoice-create-button"><Plus className="h-4 w-4 mr-1" /> Create Invoice</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>{editInvoice ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle></DialogHeader>
                      {invoiceFormJSX}
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </div>
          )}
        </div>

        <TabsContent value="invoices" className="mt-6">
          <InvoicesTable
            invoices={invoices} isAdmin={isAdmin} isStaff={isStaff} canCreate={canCreate}
            getClientName={getClientName} getFirmName={getFirmName} fmtCurrency={fmtCurrency}
            onView={setViewInvoice} onDownloadPdf={handleDownloadPdf} onEdit={handleEditInvoice}
            onDelete={handleDeleteInvoice} onStatusChange={handleInvoiceStatus}
          />
        </TabsContent>

        {/* ===== FEE PROPOSALS TAB ===== */}
        <TabsContent value="pending" className="mt-6">
          {allUninvoiced.length === 0 ? (
            <Card><CardContent className="p-8 text-center"><CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-4" /><h3 className="font-semibold mb-1">All caught up!</h3><p className="text-sm text-muted-foreground">No filed tasks pending invoicing.</p></CardContent></Card>
          ) : (
            <Card>
              <Table>
                <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Board</TableHead><TableHead>Subtask</TableHead><TableHead>Filed Periods</TableHead><TableHead>Period Range</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                <TableBody>
                  {allUninvoiced.map(item => (
                    <TableRow key={`${item.subtask_id}-${item.client_id}`} data-testid={`uninvoiced-row-${item.subtask_id}`}>
                      <TableCell className="font-medium text-sm">{item.client_name || getClientName(item.client_id)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{item.board_name}</TableCell>
                      <TableCell className="text-sm">{item.subtask_title}</TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs">{item.filed_periods.length} period(s)</Badge></TableCell>
                      <TableCell className="text-xs">{fmtPeriod(item.from_period)} to {fmtPeriod(item.to_period)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => {
                          setNewInvoice({
                            ...DEFAULT_INVOICE,
                            client_id: item.client_id,
                            service_description: item.subtask_title,
                            service_period_from: item.from_period || '',
                            service_period_to: item.to_period || '',
                          });
                          setEditInvoice(null);
                          setInvoiceDialogOpen(true);
                        }} data-testid={`raise-invoice-${item.subtask_id}`}>
                          <FileText className="h-3 w-3" /> Raise Invoice
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="proposals" className="mt-6">
          {fees.length === 0 ? (
            <Card><CardContent className="p-8 text-center"><IndianRupee className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold mb-1">No fee proposals</h3><p className="text-sm text-muted-foreground">Fee proposals will appear here.</p></CardContent></Card>
          ) : (
            <Card>
              <Table>
                <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Amount</TableHead><TableHead>Description</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead>{isClient && <TableHead>Actions</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {fees.map(fee => (
                    <TableRow key={fee.id}>
                      <TableCell className="font-medium text-sm">{getClientName(fee.client_id)}</TableCell>
                      <TableCell className="text-sm">Rs. {fmtCurrency(fee.amount)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fee.description || '-'}</TableCell>
                      <TableCell><Badge className={`text-xs ${FEE_STATUS_COLORS[fee.status]}`}>{fee.status}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(fee.created_at).toLocaleDateString()}</TableCell>
                      {isClient && fee.status === 'pending' && (
                        <TableCell><div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleFeeAction(fee.id, 'approved')} data-testid="proposal-approve-button"><CheckCircle className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleFeeAction(fee.id, 'rejected')}><XCircle className="h-4 w-4" /></Button>
                        </div></TableCell>
                      )}
                      {isClient && fee.status !== 'pending' && <TableCell>-</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* ===== FEE STRUCTURE TAB ===== */}
        {canCreate && (
          <TabsContent value="fee-structure" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Select value={feeFilterClient} onValueChange={setFeeFilterClient}>
                  <SelectTrigger className="w-[220px] h-9" data-testid="fee-structure-client-filter"><SelectValue placeholder="Filter by client" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">{filteredClientFees.length} fee(s)</span>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleDownloadFeeTemplate} data-testid="download-fee-template-button" className="text-xs gap-1">
                    <Download className="h-3.5 w-3.5" /> Download Template
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => bulkFeeInputRef.current?.click()} data-testid="upload-fee-bulk-button" className="text-xs gap-1">
                    <Upload className="h-3.5 w-3.5" /> Upload Fees
                  </Button>
                  <input ref={bulkFeeInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkFeeUpload} />
                  <Button size="sm" onClick={() => { setEditClientFee(null); setNewClientFee({ client_id: '', service_name: '', amount: '', notes: '' }); setFeeStructureDialogOpen(true); }} data-testid="add-client-fee-button">
                    <Plus className="h-4 w-4 mr-1" /> Add Fee
                  </Button>
                </div>
              )}
            </div>
            {filteredClientFees.length === 0 ? (
              <Card><CardContent className="p-8 text-center"><IndianRupee className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold mb-1">No fees configured</h3><p className="text-sm text-muted-foreground">Set up per-client fees for services to auto-fill amounts when creating invoices.</p></CardContent></Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Service / Subtask</TableHead>
                      <TableHead className="text-right">Rate / Period (Rs.)</TableHead>
                      <TableHead>Notes</TableHead>
                      {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClientFees.map(f => (
                      <TableRow key={f.id} data-testid={`client-fee-row-${f.id}`}>
                        <TableCell className="font-medium text-sm">{f.client_name || getClientName(f.client_id)}</TableCell>
                        <TableCell className="text-sm">{f.service_name}</TableCell>
                        <TableCell className="text-sm text-right font-medium">Rs. {fmtCurrency(f.amount)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{f.notes || '-'}</TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditClientFee(f); setNewClientFee({ client_id: f.client_id, service_name: f.service_name, amount: String(f.amount), notes: f.notes || '' }); setFeeStructureDialogOpen(true); }} data-testid={`edit-client-fee-${f.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteClientFee(f)} data-testid={`delete-client-fee-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="firms" className="mt-6">
            <FirmsTab
              firms={firms} firmDialogOpen={firmDialogOpen} setFirmDialogOpen={setFirmDialogOpen}
              editFirm={editFirm} setEditFirm={setEditFirm} newFirm={newFirm} setNewFirm={setNewFirm}
              logoFile={logoFile} setLogoFile={setLogoFile} EMPTY_FIRM={EMPTY_FIRM}
              onSaveFirm={handleSaveFirm} onEditFirm={handleEditFirm} onDeleteFirm={handleDeleteFirm}
            />
          </TabsContent>
        )}
      </Tabs>

      <InvoiceDetailDialog
        viewInvoice={viewInvoice} onClose={() => setViewInvoice(null)}
        getClientName={getClientName} getFirmName={getFirmName}
        fmtCurrency={fmtCurrency} onDownloadPdf={handleDownloadPdf}
      />

      {/* Manage Service Descriptions Dialog */}
      <Dialog open={descDialogOpen} onOpenChange={setDescDialogOpen}>
        <DialogContent className="max-w-md" data-testid="manage-descriptions-dialog">
          <DialogHeader><DialogTitle>Manage Service Items</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input value={newDescName} onChange={e => setNewDescName(e.target.value)} placeholder="New service description..." onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddDesc())} className="flex-1" data-testid="new-desc-input" />
              <Button size="sm" onClick={handleAddDesc} data-testid="add-desc-button"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="text-xs text-muted-foreground">Default items cannot be removed.</div>
            <ScrollArea className="h-[300px]">
              <div className="space-y-1">
                {DEFAULT_SERVICE_DESCRIPTIONS.map(d => (
                  <div key={d} className="flex items-center justify-between px-3 py-1.5 rounded bg-muted/30 text-sm"><span>{d}</span><span className="text-[10px] text-muted-foreground">Default</span></div>
                ))}
                {customDescs.map(d => (
                  <div key={d.id} className="flex items-center justify-between px-3 py-1.5 rounded bg-blue-50 text-sm" data-testid={`custom-desc-${d.id}`}>
                    <span>{d.name}</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => handleDeleteDesc(d)} data-testid={`delete-desc-${d.id}`}><X className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Fee Structure Dialog */}
      <Dialog open={feeStructureDialogOpen} onOpenChange={(open) => { setFeeStructureDialogOpen(open); if (!open) { setEditClientFee(null); setNewClientFee({ client_id: '', service_name: '', amount: '', notes: '' }); } }}>
        <DialogContent className="max-w-md" data-testid="client-fee-dialog">
          <DialogHeader><DialogTitle>{editClientFee ? 'Edit Fee' : 'Add Client Fee'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveClientFee} className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Select value={newClientFee.client_id} onValueChange={v => setNewClientFee({ ...newClientFee, client_id: v })} disabled={!!editClientFee}>
                <SelectTrigger data-testid="client-fee-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Service / Subtask *</Label>
              <Select value={newClientFee.service_name} onValueChange={v => setNewClientFee({ ...newClientFee, service_name: v })} disabled={!!editClientFee}>
                <SelectTrigger data-testid="client-fee-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>{allDescriptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rate per Period (Rs.) *</Label>
              <Input type="number" value={newClientFee.amount} onChange={e => setNewClientFee({ ...newClientFee, amount: e.target.value })} min="0" step="0.01" placeholder="0.00" required data-testid="client-fee-amount-input" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={newClientFee.notes} onChange={e => setNewClientFee({ ...newClientFee, notes: e.target.value })} placeholder="e.g. Monthly retainer" data-testid="client-fee-notes-input" />
            </div>
            <Button type="submit" className="w-full" data-testid="client-fee-submit-button">{editClientFee ? 'Update Fee' : 'Save Fee'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingPage;
